import React, { useState } from 'react';
import { Heart, MessageCircle, Mail, Loader2, Send, User as UserIcon, MoreHorizontal, Flag, Ban } from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from './ui/dropdown-menu';
import { community as commApi, files as filesApi } from '../services/api';
import { toast } from '../hooks/use-toast';

export const Silhouette = ({ size = 40 }) => (
  <div className="rounded-full bg-gray-500/40 flex items-center justify-center shrink-0" style={{ width: size, height: size }} data-testid="visitor-silhouette">
    <UserIcon size={size * 0.55} className="text-gray-300" />
  </div>
);

const Author = ({ item, isMember, size = 40 }) => (
  <div className="flex items-center gap-3">
    {isMember
      ? <img src={filesApi.urlFor(item.avatar)} alt="" className="rounded-full object-cover" style={{ width: size, height: size }} />
      : <Silhouette size={size} />}
    <div>
      <div className="text-sm font-semibold text-white">
        {item.user}{!isMember && item.state ? <span className="font-normal text-gray-300">, {item.state}</span> : null}
      </div>
      {!isMember && <div className="text-[11px] text-gray-400">Photos are viewable by members only</div>}
    </div>
  </div>
);

export const SafetyMenu = ({ target, safety, testId }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button className="p-1 rounded text-gray-400 hover:text-white hover:bg-white/10" data-testid={testId} aria-label="More options"><MoreHorizontal size={16} /></button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="bg-[#111] border-[#2a2a2a] text-white">
      <DropdownMenuItem onClick={() => safety.report(target)} data-testid={`${testId}-report`} className="gap-2"><Flag size={14} /> Report {target.name}</DropdownMenuItem>
      <DropdownMenuItem onClick={() => safety.block(target)} data-testid={`${testId}-block`} className="gap-2 text-red-400"><Ban size={14} /> Block {target.name}</DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

function CommentsSection({ post, isMember, onRequireJoin, onAdded, viewer, safety }) {
  const [items, setItems] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  React.useEffect(() => { commApi.comments(post.id).then(setItems).catch(() => setItems([])); }, [post.id]);

  const send = async () => {
    if (!isMember) return onRequireJoin();
    if (!text.trim()) return;
    setSending(true);
    try {
      const c = await commApi.comment(post.id, text.trim());
      setItems(prev => [...(prev || []), c]);
      setText('');
      onAdded();
    } catch (err) {
      toast({ title: 'Comment failed', description: err?.response?.data?.detail || 'Please try again.' });
    } finally { setSending(false); }
  };

  return (
    <div className="mt-3 pt-3 border-t border-[#1f1f1f] space-y-3" data-testid={`comments-section-${post.id}`}>
      {items === null ? <Loader2 className="animate-spin text-gray-400" size={16} />
        : items.length === 0 ? <div className="text-xs text-gray-400" data-testid={`comments-empty-${post.id}`}>No comments yet. Be the first to reply.</div>
        : items.map(c => (
          <div key={c.id} className="bg-white/5 rounded-lg p-3" data-testid={`comment-${c.id}`}>
            <div className="flex items-start justify-between gap-2">
              <Author item={c} isMember={isMember} size={28} />
              {isMember && c.userId && c.userId !== viewer.id && (
                <SafetyMenu target={{ userId: c.userId, name: c.user, commentId: c.id }} safety={safety} testId={`comment-menu-${c.id}`} />
              )}
            </div>
            <div className="mt-2 text-sm text-gray-200 whitespace-pre-wrap">{c.text}</div>
          </div>
        ))}
      <div className="flex gap-2">
        <input value={text} onChange={e => setText(e.target.value)} onFocus={() => !isMember && onRequireJoin()}
          onKeyDown={e => e.key === 'Enter' && send()} maxLength={1000}
          className="pbb-input flex-1" placeholder={isMember ? 'Write a public comment...' : 'Join or sign in to comment'}
          data-testid={`comment-input-${post.id}`} />
        <button onClick={send} disabled={sending} data-testid={`comment-submit-${post.id}`}
          className="px-3 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm inline-flex items-center gap-1 disabled:opacity-70">
          {sending ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} Reply
        </button>
      </div>
    </div>
  );
}

export default function CommunityPostCard({ post, viewer, onLike, onRequireJoin, onMessage, onCommentAdded, safety }) {
  const [open, setOpen] = useState(false);
  const isMember = !!viewer;
  const canMessage = isMember && post.userId && post.userId !== viewer.id;

  return (
    <div className="pbb-card p-4" data-testid={`community-post-${post.id}`}>
      <div className="flex items-start justify-between gap-3">
        <Author item={post} isMember={isMember} />
        <div className="flex items-center gap-2 shrink-0">
          <div className="text-xs text-gray-400">{post.time}{post.time !== 'now' ? ' ago' : ''}</div>
          {canMessage && <SafetyMenu target={{ userId: post.userId, name: post.user, postId: post.id }} safety={safety} testId={`post-menu-${post.id}`} />}
        </div>
      </div>
      <div className="mt-3 text-sm text-gray-200 whitespace-pre-wrap">{post.text}</div>
      {post.image && <img src={filesApi.urlFor(post.image)} alt="" className="mt-3 rounded-lg w-full max-h-96 object-cover" />}
      <div className="flex items-center gap-5 mt-3 text-sm text-gray-300">
        <button onClick={() => (isMember ? onLike(post.id) : onRequireJoin())} data-testid={`post-like-${post.id}`} className="inline-flex items-center gap-1 hover:text-green-400"><Heart size={16} /> {post.likes}</button>
        <button onClick={() => setOpen(o => !o)} data-testid={`post-comment-toggle-${post.id}`} className="inline-flex items-center gap-1 hover:text-green-400"><MessageCircle size={16} /> {post.comments}</button>
        {(canMessage || !isMember) && (
          <button onClick={() => (isMember ? onMessage(post) : onRequireJoin())} data-testid={`post-message-${post.id}`} className="inline-flex items-center gap-1 hover:text-green-400"><Mail size={16} /> Message privately</button>
        )}
      </div>
      {open && <CommentsSection post={post} isMember={isMember} viewer={viewer} safety={safety} onRequireJoin={onRequireJoin} onAdded={() => onCommentAdded(post.id)} />}
    </div>
  );
}
